import styled, { css, keyframes } from 'styled-components';

import { CUST, CUST_ANIM, CUST_SHADOWS } from '../../../../theme/customizer-tokens';
import type { GhostKind } from '../lib/morph';

/** Constant radius (Figma 18) — already > the bar's half-height, so the box
 *  flattens to a perfect pill end with no corner-pop. Geometry, not colour. */
const RADIUS = 18;
/** The ghost globe glyph renders at the chrome's NATIVE size (16) and starts
 *  scaled UP to the intro's 18px, animating down to scale(1). So the forward
 *  landing is an identity transform — pixel-identical to the real 16px globe, with
 *  no transform-rasterisation difference left to snap at the handoff. */
const GLOBE_START_SCALE = 18 / 16;
/** Fraction of the flight over which the numeral clears (forward) / arrives
 *  (reverse) — kept short so it never half-squashes against the flattening box. */
const GLYPH_PCT = `${(CUST_ANIM.MORPH_GLYPH_FADE_MS / CUST_ANIM.MORPH_FLIGHT_MS) * 100}%`;

/** Fixed overlay covering the morph container; the four ghosts fly within it.
 *  Non-interactive — the underlying step owns all input. */
export const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
`;

/** A zero-size point pinned (inline left/top) to the source box's CENTRE; its
 *  skin child is FLEX-centred on it, so resizing the skin collapses it
 *  symmetrically around that point and the measured translate lands it exactly on
 *  the destination centre. (Flex centres the item within the 0-size CONTAINER, i.e.
 *  on the point; grid `place-items` would centre it within its content-sized TRACK
 *  instead — offsetting it by half the box and snapping at the handoff.) Carries
 *  the per-box CSS vars the keyframes read: the dx/dy travel, the from/to/mid-h
 *  sizes, and the delay stagger. */
export const Ghost = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Each box flies on the ONE shared screen ease. Height is held high to ~72% of
// the way (--mid-h) then collapses late, so the box travels mostly square and
// flattens only as it decelerates into the rail. Width is constant for the bars
// (46→46) and only shrinks for the globe. Colour endpoints are themed tokens, so
// the morph is light/dark-correct and free of raw colour literals. Going BACK
// replays these in reverse (animation-direction: reverse) — bars expand back into
// boxes — so the same measured geometry serves both directions.
const flightHero = keyframes`
  0%   { transform: translate(0, 0); width: var(--from-w); height: var(--from-h); }
  50%  { height: var(--mid-h); }
  100% { transform: translate(var(--dx), var(--dy)); width: var(--to-w); height: var(--to-h); }
`;

const flightIdle = keyframes`
  0%   { transform: translate(0, 0); width: var(--from-w); height: var(--from-h); background: ${CUST.stageNumIdle}; }
  50%  { height: var(--mid-h); background: ${CUST.stageNumIdle}; }
  100% { transform: translate(var(--dx), var(--dy)); width: var(--to-w); height: var(--to-h); background: ${CUST.mobileTrack}; }
`;

// The globe deliberately has NO mid-h late-flatten keyframe (unlike the bars): it
// shrinks straight to the 16px glyph slot while its plate dissolves, so the
// surviving glyph reads as the anchor rather than a flattening bar.
const flightGlobe = keyframes`
  0%   { transform: translate(0, 0); width: var(--from-w); height: var(--from-h); background: ${CUST.stageNumIdle}; }
  100% { transform: translate(var(--dx), var(--dy)); width: var(--to-w); height: var(--to-h); background: ${CUST.stageNumIdleTransparent}; }
`;

// The single cinematic accent: a faint warm comet trailing the leading orange
// box as it flies up, fully gone before it lands matte. Forward-only (reverse
// would otherwise hold a glow at the box and snap it off at the handoff).
const heroGlow = keyframes`
  from { box-shadow: ${CUST_SHADOWS.morphHeroGlow}; }
  to   { box-shadow: ${CUST_SHADOWS.morphHeroGlowSoft}; }
`;

const FLIGHT = `${CUST_ANIM.MORPH_FLIGHT_MS}ms ${CUST_ANIM.SCREEN_EASE} var(--delay) both`;
const dirRule = ($reverse?: boolean) => css`
  animation-direction: ${$reverse ? 'reverse' : 'normal'};
`;

const skinCss = ($kind: GhostKind, $reverse?: boolean) => {
  if ($kind === 'hero') {
    return $reverse
      ? css`
          background: ${CUST.accent};
          animation: ${flightHero} ${FLIGHT};
          ${dirRule(true)}
        `
      : css`
          background: ${CUST.accent};
          animation:
            ${flightHero} ${FLIGHT},
            ${heroGlow} ${CUST_ANIM.MORPH_GLOW_MS}ms ease-in-out var(--delay) both;
          ${dirRule(false)}
        `;
  }
  if ($kind === 'globe') {
    return css`
      background: ${CUST.stageNumIdle};
      animation: ${flightGlobe} ${FLIGHT};
      ${dirRule($reverse)}
    `;
  }
  return css`
    background: ${CUST.stageNumIdle};
    animation: ${flightIdle} ${FLIGHT};
    ${dirRule($reverse)}
  `;
};

export const Skin = styled.div<{ $kind: GhostKind; $reverse?: boolean }>`
  /* flex:none so the 0-size Ghost's flex-shrink can't collapse the box — it keeps
     its animated width/height. (grid centres the glyph child inside this SIZED box,
     which is correct; only the parent Ghost needed flex.) */
  flex: 0 0 auto;
  width: var(--from-w);
  height: var(--from-h);
  border-radius: ${RADIUS}px;
  display: grid;
  place-items: center;
  /* Only transform can be layer-promoted; the box also animates width/height but
     that reflow is contained to this 4-element overlay and never touches the page. */
  will-change: transform;
  ${({ $kind, $reverse }) => skinCss($kind, $reverse)}
`;

// Full-flight keyframes with the fade packed into the first GLYPH_PCT, so reverse
// (played backward) makes the numeral ARRIVE late as the box re-forms.
const numFade = keyframes`
  0%          { opacity: 1; transform: scale(1); }
  ${GLYPH_PCT} { opacity: 0; transform: scale(0.7); }
  100%        { opacity: 0; transform: scale(0.7); }
`;

/** The numeral collapses toward the flattening centreline and clears before the
 *  box drops below it — never a clipped half-digit. Black on the hero, grey idle,
 *  matching the real boxes exactly so the handoff is seamless. */
export const Num = styled.span<{ $hero: boolean; $reverse?: boolean }>`
  font-size: 17px;
  font-weight: 700;
  color: ${({ $hero }) => ($hero ? CUST.onAccent : CUST.stageNumIdleText)};
  animation: ${numFade} ${CUST_ANIM.MORPH_FLIGHT_MS}ms ${CUST_ANIM.MORPH_OUT_EASE} var(--delay) both;
  ${({ $reverse }) => dirRule($reverse)}
`;

const globeMorph = keyframes`
  from { transform: scale(${GLOBE_START_SCALE}); color: ${CUST.stageNumIdleText}; }
  to   { transform: scale(1); color: ${CUST.mobileTrack}; }
`;

/** The one element that survives the whole morph: the globe glyph never fades —
 *  it only scales 18→16 (landing at scale(1), pixel-exact) and tints to the
 *  off-track colour while its grey plate dissolves around it, an unbroken anchor. */
export const GlobeGlyph = styled.span<{ $reverse?: boolean }>`
  display: inline-flex;
  color: ${CUST.stageNumIdleText};
  animation: ${globeMorph} ${FLIGHT};
  ${({ $reverse }) => dirRule($reverse)}
`;
